import React, { useState, useEffect, useCallback, useContext } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import * as moment from "moment-timezone";
import { useAppContextNonNull } from "@contexts/AppContext";
import UiContext from "../../../contexts/UiContext";
import { buildRestUrl, buildAuthHeadersForFetch } from "../../../services/RestService";
import RestEndpointsEnum from "../../../models/RestEndpointsEnum";
import IDaily_Route from "../../../models/IDaily_Route";
import IDaily_Route_Order_Map from "../../../models/IDaily_Route_Order_Map";
import { StopStatus } from "../../../models/StopStatusEnum";
import OwTitle from "../../shared/OwTitle/OwTitle";
import OwLabel from "../../shared/OwLabel/OwLabel";
import OwButton from "../../shared/OwButton/OwButton";
import Breadcrumbs from "../../shared/Breadcrumbs/Breadcrumbs";
import { DailyRouteMapModal } from "../DailyRouteMapModal/DailyRouteMapModal";
import { ViewDeliveryProofModal } from "../../shared/ViewDeliveryProofModal/ViewDeliveryProofModal";
import "./DailyRouteDetail.scss";
import OrderStatusPill from "@components/shared/OrderStatusPill";
import { OwIconTooltip } from "@components/shared/OwIconTooltip/OwIconTooltip";
import { OwTooltip } from "@components/shared/OwTooltip/OwTooltip";
import { OwContinueConfirmModal } from "../../shared/OwContinueConfirmModal/OwContinueConfirmModal";
import DailyRouteService from "../../../services/DailyRouteService";
import INotification from "../../../models/INotification";
import NotificationLevelEnum from "../../../models/NotificationLevelEnum";
import NotificationPositionEnum from "../../../models/NotificationPositionEnum";

const StopStatusBadge = ({ status, reason }: { status?: StopStatus; reason?: string | null }) => {
    const getStatusConfig = (status?: StopStatus) => {
        switch (status) {
            case "completed":
                return { icon: "fas fa-check-circle", className: "status-completed", label: "completed" };
            case "arrived":
                return { icon: "fas fa-circle", className: "status-arrived", label: "arrived" };
            case "failed":
                return { icon: "fas fa-times-circle", className: "status-failed", label: "failed" };
            case "skipped":
                return { icon: "fas fa-minus-circle", className: "status-skipped", label: "skipped" };
            case "pending":
            default:
                return { icon: "far fa-circle", className: "status-pending", label: "pending" };
        }
    };

    const config = getStatusConfig(status);
    const className = `stop-status-badge ${config.className}`;

    const content = (
        <>
            <i className={config.icon} />
            <span className="status-label">
                <OwLabel title={config.label} />
            </span>
            {reason && <span className="status-reason">({reason})</span>}
        </>
    );

    // The reason is ellipsed in the cell, so show it in full on hover.
    if (reason) {
        return (
            <OwTooltip className={className} tooltipContent={reason}>
                {content}
            </OwTooltip>
        );
    }

    return <div className={className}>{content}</div>;
};

export const DailyRouteDetail = () => {
    const { guid } = useParams<{ guid: string }>();
    const { userModel, supplierModel } = useAppContextNonNull();
    const uiContext = useContext(UiContext);
    const navigate = useNavigate();

    const [dailyRoute, setDailyRoute] = useState<IDaily_Route | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [showMapModal, setShowMapModal] = useState(false);
    const [isCompleting, setIsCompleting] = useState(false);

    const hasDeliveryProof = (stop: IDaily_Route_Order_Map): boolean => {
        return !!(stop.Signature || (stop.Photos && stop.Photos.length > 0));
    };

    const openDeliveryProofModal = (stop: IDaily_Route_Order_Map) => {
        if (!stop.Order) return;
        uiContext.setModalSetup({
            modalContentClass: "modal-dialog-centered modal-lg",
            modalOverlayClass: "modalOverlayClass d-flex align-items-center",
        });
        uiContext.setModalComponent(
            <ViewDeliveryProofModal orderName={stop.Order.name || ""} signature={stop.Signature} photos={stop.Photos} />
        );
        uiContext.showModal(true);
    };

    const fetchDailyRoute = useCallback(async () => {
        if (!guid) return;

        setIsLoading(true);
        setError(null);

        try {
            const url = buildRestUrl({
                endpoint: RestEndpointsEnum.DailyRoute,
                params: { dailyRouteGuid: guid },
            });

            const headers = buildAuthHeadersForFetch({ userModel, supplierModel });
            const response = await fetch(url, { method: "GET", headers });

            if (!response.ok) {
                throw new Error(`Failed to fetch route: ${response.statusText}`);
            }

            const data = await response.json();
            setDailyRoute(data.dailyRoute || data);
        } catch (err) {
            setError(err instanceof Error ? err.message : "An error occurred");
        } finally {
            setIsLoading(false);
        }
    }, [guid, userModel, supplierModel]);

    useEffect(() => {
        fetchDailyRoute();
    }, [fetchDailyRoute]);

    const handleDownloadCSV = async () => {
        if (!dailyRoute?.guid) return;

        try {
            const url = buildRestUrl({
                endpoint: RestEndpointsEnum.DailyRouteCSV,
                params: { dailyRouteGuid: dailyRoute.guid },
            });

            const headers = buildAuthHeadersForFetch({ userModel, supplierModel });
            const response = await fetch(url, { method: "GET", headers });

            if (!response.ok) {
                throw new Error("Failed to download CSV");
            }

            const blob = await response.blob();
            const downloadUrl = window.URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = downloadUrl;
            a.download = `route-${dailyRoute.Route?.name || "export"}-${dayjs(dailyRoute.route_date).format("YYYY-MM-DD")}.csv`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            window.URL.revokeObjectURL(downloadUrl);
        } catch (err) {
            console.error("CSV download error:", err);
        }
    };

    const closeModal = () => {
        uiContext.setModalComponent(null);
        uiContext.showModal(false);
    };

    const notify = (message: string, level: NotificationLevelEnum) => {
        const notification: INotification = {
            message,
            position: NotificationPositionEnum.bc,
            level,
        };

        uiContext.notifyFn(null, notification);
    };

    const completeRoute = async () => {
        closeModal();

        if (!dailyRoute?.guid) return;

        setIsCompleting(true);

        try {
            const url = buildRestUrl({
                endpoint: RestEndpointsEnum.DailyRouteAdminComplete,
                params: { dailyRouteGuid: dailyRoute.guid },
            });

            const headers = buildAuthHeadersForFetch({ userModel, supplierModel });
            const response = await fetch(url, { method: "POST", headers });

            if (!response.ok) {
                throw new Error(`Failed to complete route: ${response.statusText}`);
            }

            notify("route_marked_completed", NotificationLevelEnum.success);
            await fetchDailyRoute();
        } catch (err) {
            console.error("Complete route error:", err);
            notify("failed_to_complete_route", NotificationLevelEnum.error);
        } finally {
            setIsCompleting(false);
        }
    };

    // Same flow as the Enroute app: warn when unfinished stops will be auto-skipped.
    const confirmCompleteRoute = () => {
        const hasIncompleteStops = DailyRouteService.getIncompleteStops(dailyRoute?.Daily_Route_Order_Maps).length > 0;

        // Set explicitly: showModal(false) doesn't reset the classes, so a prior delivery-proof
        // modal would otherwise leave its classes (and no theme background) behind.
        uiContext.setModalSetup({
            modalContentClass: "modalContentClass",
            modalOverlayClass: "modalOverlayClass",
        });
        uiContext.setModalComponent(
            <OwContinueConfirmModal
                title="complete_route"
                message={hasIncompleteStops ? "complete_route_incomplete_stops_confirm" : "complete_route_confirm"}
                acceptTitle={hasIncompleteStops ? "complete_anyway" : "complete_route"}
                acceptFn={completeRoute}
                cancelFn={closeModal}
                preserveNewlines={true}
            />
        );
        uiContext.showModal(true);
    };

    const getSortedStops = (): IDaily_Route_Order_Map[] => {
        if (!dailyRoute?.Daily_Route_Order_Maps) return [];

        // Stops with a cleared/missing optimized_order_seq (e.g. a driver-overridden,
        // optimizer-excluded stop) sort to the bottom, then chronologically by when they
        // were actually progressed — matching what really happened on the route.
        const seqOf = (s: IDaily_Route_Order_Map) =>
            s.optimized_order_seq !== null && s.optimized_order_seq !== undefined ? s.optimized_order_seq : Number.MAX_SAFE_INTEGER;
        const timeOf = (s: IDaily_Route_Order_Map) => {
            const t = s.completed_at || s.arrived_at;
            return t ? new Date(t).getTime() : Number.MAX_SAFE_INTEGER;
        };

        return [...dailyRoute.Daily_Route_Order_Maps].sort((a, b) => {
            const seqDiff = seqOf(a) - seqOf(b);
            if (seqDiff !== 0) return seqDiff;
            return timeOf(a) - timeOf(b);
        });
    };

    const getCompletionStats = () => {
        const stops = dailyRoute?.Daily_Route_Order_Maps || [];
        const completed = stops.filter((s) => s.status === "completed").length;
        const failed = stops.filter((s) => s.status === "failed").length;
        const skipped = stops.filter((s) => s.status === "skipped").length;
        return { total: stops.length, completed, failed, skipped };
    };

    const getSequenceNumber = (stop: IDaily_Route_Order_Map, index: number) => {
        if (stop.optimized_order_seq !== null && stop.optimized_order_seq !== undefined) {
            return stop.optimized_order_seq + 1; // it's 0-based
        }
        return index + 1;
    };

    if (isLoading) {
        return (
            <div className="DailyRouteDetail">
                <div className="loading-state">
                    <i className="fas fa-spinner fa-spin fa-2x" />
                    <div className="mt-2">
                        <OwLabel title="loading" />
                    </div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="DailyRouteDetail">
                <div className="error-state">
                    <i className="fas fa-exclamation-triangle fa-2x text-danger" />
                    <div className="mt-2">{error}</div>
                    <OwButton title="retry" className="btn-primary mt-3" fn={fetchDailyRoute} />
                </div>
            </div>
        );
    }

    if (!dailyRoute) {
        return (
            <div className="DailyRouteDetail">
                <div className="error-state">
                    <i className="fas fa-route fa-2x" />
                    <div className="mt-2">
                        <OwLabel title="route_not_found" />
                    </div>
                </div>
            </div>
        );
    }

    const stats = getCompletionStats();
    const sortedStops = getSortedStops();
    const canCompleteRoute = dailyRoute.status !== "completed" && DailyRouteService.canAdminCompleteRoute(userModel);

    const plannedStart =
        dailyRoute.planned_start_time && supplierModel.timezone
            ? moment.utc(dailyRoute.planned_start_time).tz(supplierModel.timezone).format("h:mm A z")
            : null;

    const actualStart =
        dailyRoute.started_at && supplierModel.timezone
            ? moment.utc(dailyRoute.started_at).tz(supplierModel.timezone).format("h:mm A z")
            : null;

    return (
        <div className="DailyRouteDetail">
            {/* Breadcrumbs */}
            <Breadcrumbs
                crumbs={[
                    { title: "daily_routes", url: "/routes/daily" },
                    { title: dailyRoute.name || "Route Detail", url: "", skipTranslate: true },
                ]}
            />

            {/* Header */}
            <div className="detail-header mt-4">
                <div className="header-info">
                    <OwTitle title={dailyRoute.name || "Route"} tag="h2" disableTranslation={true} defaultMargins={false} />
                    <OwTitle title={dailyRoute.Route?.name || "Route"} tag="h4" disableTranslation={true} defaultMargins={false} />
                    <div className="route-date">
                        <i className="far fa-calendar-alt me-2" />
                        {dayjs(dailyRoute.route_date).format("dddd, MMMM D, YYYY")}
                    </div>
                    {plannedStart && (
                        <div className="planned-start">
                            <i className="far fa-clock me-2" />
                            <OwLabel title="planned_start_time" />: {plannedStart}
                        </div>
                    )}
                    {actualStart && (
                        <div className="actual-start">
                            <i className="fas fa-clock me-2" />
                            <OwLabel title="actual_start_time" />: {actualStart}
                        </div>
                    )}
                    <div>
                        {dailyRoute.is_optimized && (
                            <span className="optimized-badge">
                                <i className="fas fa-check-circle me-1" />
                                <OwLabel title="optimized" />
                            </span>
                        )}
                        <span
                            className={`${dailyRoute.is_optimized ? "ms-4" : ""} route-status-badge status-${dailyRoute.status || "not_started"}`}>
                            <i
                                className={
                                    dailyRoute.status === "completed"
                                        ? "fas fa-check-circle me-1"
                                        : dailyRoute.status === "in_progress"
                                          ? "fas fa-truck me-1"
                                          : "far fa-circle me-1"
                                }
                            />
                            <OwLabel title={dailyRoute.status || "not_started"} />
                        </span>
                    </div>
                </div>
                <div className="header-actions">
                    <OwButton
                        title="view_map"
                        leftIconClass="fas fa-map-marked-alt"
                        className="btn-primary"
                        fn={() => setShowMapModal(true)}
                        disabled={!sortedStops.length}
                    />
                    {supplierModel.has_van_sell && dailyRoute.Vehicle && (
                        <OwButton
                            title="eod_reconciliation"
                            leftIconClass="fas fa-clipboard-check"
                            className="btn-secondary"
                            fn={() => navigate(`/routes/daily/${dailyRoute.guid}/eod`)}
                        />
                    )}
                    <OwButton title="download_csv" leftIconClass="fas fa-download" className="btn-secondary" fn={handleDownloadCSV} />
                    {canCompleteRoute && (
                        <OwButton
                            title="complete_route"
                            leftIconClass="fas fa-flag-checkered"
                            className="btn-success"
                            fn={confirmCompleteRoute}
                            disabled={isCompleting}
                        />
                    )}
                </div>
            </div>

            {/* Info Cards */}
            <div className="info-cards-row">
                {/* Driver Card */}
                <div className="info-card driver-card">
                    <div className="card-header">
                        <i className="fas fa-user-tie me-2" />
                        <OwLabel title="driver" />
                    </div>
                    <div className="card-body">
                        {dailyRoute.Driver ? (
                            <>
                                <div className="info-row primary">
                                    <span className="info-value">{dailyRoute.Driver.name}</span>
                                </div>
                                {dailyRoute.Driver.phone && (
                                    <div className="info-row">
                                        <i className="fas fa-phone me-2" />
                                        <a href={`tel:${dailyRoute.Driver.phone}`} className="contact-link">
                                            {dailyRoute.Driver.phone}
                                        </a>
                                    </div>
                                )}
                                {dailyRoute.Driver.email && (
                                    <div className="info-row">
                                        <i className="fas fa-envelope me-2" />
                                        <a href={`mailto:${dailyRoute.Driver.email}`} className="contact-link">
                                            {dailyRoute.Driver.email}
                                        </a>
                                    </div>
                                )}
                            </>
                        ) : (
                            <div className="not-assigned">
                                <OwLabel title="no_driver_assigned" />
                            </div>
                        )}
                    </div>
                </div>

                {/* Vehicle Card */}
                <div className="info-card vehicle-card">
                    <div className="card-header">
                        <i className="fas fa-truck me-2" />
                        <OwLabel title="vehicle" />
                    </div>
                    <div className="card-body">
                        {dailyRoute.Vehicle ? (
                            <div className="info-row primary">
                                <span className="info-value">{dailyRoute.Vehicle.name}</span>
                            </div>
                        ) : (
                            <div className="not-assigned">
                                <OwLabel title="no_vehicle_assigned" />
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Stops Section */}
            <div className="stops-section">
                <div className="stops-header">
                    <div className="stops-title">
                        <i className="fas fa-map-marker-alt me-2" />
                        <OwLabel title="stops" />
                        <span className="stops-count">({stats.total})</span>
                    </div>
                    <div className="stops-progress">
                        <span className="progress-item completed">
                            <i className="fas fa-check-circle me-1" />
                            {stats.completed} <OwLabel title="completed" className="ms-1" />
                        </span>
                        {stats.failed > 0 && (
                            <span className="progress-item failed">
                                <i className="fas fa-times-circle me-1" />
                                {stats.failed} <OwLabel title="failed" className="ms-1" />
                            </span>
                        )}
                        {stats.skipped > 0 && (
                            <span className="progress-item skipped">
                                <i className="fas fa-minus-circle me-1" />
                                {stats.skipped} <OwLabel title="skipped" className="ms-1" />
                            </span>
                        )}
                    </div>
                </div>

                {sortedStops.length === 0 ? (
                    <div className="no-stops">
                        <i className="fas fa-inbox fa-2x mb-2" />
                        <div>
                            <OwLabel title="no_stops_on_route" />
                        </div>
                    </div>
                ) : (
                    <div className="stops-list">
                        <div className="stops-table-header">
                            <div className="col-seq">#</div>
                            <div className="col-status">
                                <OwLabel title="status" />
                            </div>
                            <div className="col-order">
                                <OwLabel title="order" />
                            </div>
                            <div className="col-customer">
                                <OwLabel title="customer" />
                            </div>
                            <div className="col-address">
                                <OwLabel title="address" />
                            </div>
                            <div className="col-service">
                                <OwLabel title="service_minutes" />
                            </div>
                            <div className="col-notes">
                                <OwLabel title="notes" />
                            </div>
                            <div className="col-proof">
                                <OwLabel title="proof" />
                            </div>
                        </div>
                        {sortedStops.map((stop, index) => {
                            // Order-less customer stops (van-sell DSD) carry their identity
                            // and address on the stop-level Vendor; Order is null there.
                            const shipTo = stop.Order?.Ship_To || stop.Vendor?.Ship_To;

                            return (
                                <div
                                    key={stop.Order?.guid || stop.Vendor?.guid || index}
                                    className={`stop-row ${stop.status || "pending"}`}>
                                    <div className="col-seq">
                                        <span className="seq-number">{getSequenceNumber(stop, index)}</span>
                                    </div>
                                    <div className="col-status">
                                        <StopStatusBadge status={stop.status} reason={stop.status_reason} />
                                        {stop.was_reopt_overridden && (
                                            <OwIconTooltip
                                                iconClassName="fas fa-exclamation-triangle text-warning ms-2"
                                                tooltipContent="driver_overrode_skipped_status"
                                                placement="top"
                                                translateContent={true}
                                            />
                                        )}
                                    </div>
                                    <div className="col-order">
                                        {stop.Order ? (
                                            <div>
                                                <Link to={`/orders/${stop.Order.guid}/entry`} className="order-link">
                                                    {stop.Order.name}
                                                    <i className="fas fa-external-link-alt ms-1" />
                                                </Link>
                                                <OrderStatusPill orderState={stop.Order.Order_State} />
                                            </div>
                                        ) : (
                                            "-"
                                        )}
                                    </div>
                                    <div className="col-customer">{stop.Order?.Vendor?.name || stop.Vendor?.name || "-"}</div>
                                    <div className="col-address">
                                        {shipTo ? (
                                            <div className="address-display">
                                                <div>{shipTo.address_line_one}</div>
                                                <div className="address-city">
                                                    {shipTo.city}, {shipTo.state} {shipTo.postal_code}
                                                </div>
                                            </div>
                                        ) : (
                                            "-"
                                        )}
                                    </div>
                                    <div className="col-service">
                                        {DailyRouteService.getEffectiveServiceMinutes(
                                            stop.service_minutes,
                                            supplierModel.default_stop_service_minutes,
                                            stop.Order?.Vendor?.default_service_minutes ?? stop.Vendor?.default_service_minutes
                                        )}{" "}
                                        min
                                        {stop.service_minutes !== null && stop.service_minutes !== undefined && (
                                            <OwIconTooltip
                                                iconClassName="fas fa-pen text-muted ms-2"
                                                tooltipContent="service_time_overridden"
                                                placement="top"
                                                translateContent={true}
                                            />
                                        )}
                                    </div>
                                    <div className="col-notes">
                                        {stop.driver_notes && (
                                            <span className="driver-notes" title={stop.driver_notes}>
                                                <i className="fas fa-sticky-note me-1" />
                                                {stop.driver_notes}
                                            </span>
                                        )}
                                    </div>
                                    <div className="col-proof">
                                        {hasDeliveryProof(stop) && (
                                            <button
                                                className="proof-button"
                                                onClick={() => openDeliveryProofModal(stop)}
                                                title="View delivery proof">
                                                {stop?.Signature && <i className="fas fa-signature me-1" />}
                                                {stop?.Photos && stop.Photos.length > 0 && <i className="fas fa-camera" />}
                                            </button>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            {/* Map Modal */}
            <DailyRouteMapModal dailyRoute={dailyRoute} show={showMapModal} onHide={() => setShowMapModal(false)} />
        </div>
    );
};
